import React, { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  Text,
  Badge,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuItemCheckbox,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from '@fluentui/react-components';
import { FilterRegular } from '@fluentui/react-icons';
import StockItemDetailsPane from '../components/StockItemDetailsPane';
import { useDebug } from '../contexts/DebugContext';
import { fetchAllCustomerLocations } from '../services/customerLocationService';
import { apiFetch } from '../lib/apiFetch';
import { formatCalendarDate, formatBusinessDate } from '../lib/businessTime';
import { getMapConnector } from '../../../shared/maps';
import type { MapHandle } from '../../../shared/maps';

// Phase 4 (#325) migration: was importing `mapbox-gl` + setting
// `mapboxgl.accessToken` directly. Now routed through the active connector
// (`getMapConnector()`) so flipping `VITE_MAPS_PROVIDER` between
// `google` and `mapbox` swaps providers without code change.
const { MapView, Marker } = getMapConnector();

// Puerto Rico bounds for generating unique locations
const PR_BOUNDS = {
  minLng: -67.27,
  maxLng: -65.59,
  minLat: 17.93,
  maxLat: 18.52,
};

// Color palette for product groups - vibrant, distinguishable colors
const COLOR_PALETTE = [
  '#0078d4', // Blue
  '#d13438', // Red
  '#107c10', // Green
  '#8764b8', // Purple
  '#ff8c00', // Orange
  '#00b7c3', // Cyan
  '#e3008c', // Magenta
  '#498205', // Olive Green
  '#00188f', // Navy Blue
  '#00bcf2', // Sky Blue
  '#bad80a', // Lime
  '#ca5010', // Rust
  '#8e562e', // Brown
  '#0099bc', // Teal
  '#ffaa44', // Gold
];

// Generate a consistent color for a product group based on its name
const getProductGroupColor = (productGroupName: string): string => {
  let hash = 0;
  for (let i = 0; i < productGroupName.length; i++) {
    hash = ((hash << 5) - hash) + productGroupName.charCodeAt(i);
    hash = hash & hash;
  }
  const index = Math.abs(hash) % COLOR_PALETTE.length;
  return COLOR_PALETTE[index];
};

// Generate a unique location for each item based on its ID (within Puerto Rico bounds)
const getUniqueLocation = (itemId: string): { coordinates: [number, number], name: string } => {
  // Create a deterministic hash from the item ID
  let hash = 0;
  for (let i = 0; i < itemId.length; i++) {
    hash = ((hash << 5) - hash) + itemId.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Use the hash to generate coordinates within Puerto Rico bounds
  // Use different parts of the hash for lng and lat to ensure more variation
  const lngHash = Math.abs(hash);
  const latHash = Math.abs(hash >> 16);

  const lng = PR_BOUNDS.minLng + (lngHash % 10000) / 10000 * (PR_BOUNDS.maxLng - PR_BOUNDS.minLng);
  const lat = PR_BOUNDS.minLat + (latHash % 10000) / 10000 * (PR_BOUNDS.maxLat - PR_BOUNDS.minLat);

  return {
    coordinates: [lng, lat],
    name: 'Puerto Rico'
  };
};

interface ProductGroup {
  id: string;
  name: string;
  productType: string;
  photoUrl?: string;
  sku: string;
  stock_count?: number;
}

interface StockItem {
  id: string;
  identifier: string;
  status: string;
  productId: string;
  productName?: string;
  productPhotoUrl?: string;
  availableFrom?: string;
  availableTill?: string;
  createdAt?: string;
  barcode?: string;
  productGroupName?: string;
  productGroupPhotoUrl?: string;
}

interface FleetVehicle {
  id: string;
  name: string;
  type: string;
  licensePlate: string;
  status: 'active' | 'maintenance' | 'inactive';
  location?: { lat: number; lng: number };
}

interface CustomerLocation {
  id: string;
  customerId: string;
  name: string;
  label: string;
  address: string;
  location: { lat: number; lng: number };
  customerType: 'individual' | 'company';
}

// Load customer locations from the new /customer-locations bulk endpoint and
// pick each customer's primary site (oldest, which matches the Primary badge
// in the Customer Profile Locations tab). Returns one pin per customer.
const loadPrimaryCustomerLocations = async (): Promise<CustomerLocation[]> => {
  const rows = await fetchAllCustomerLocations();
  const seenCustomers = new Set<string>();
  const primaries: CustomerLocation[] = [];
  for (const row of rows) {
    if (seenCustomers.has(row.customer_id)) continue;
    if (row.latitude == null || row.longitude == null) continue;
    seenCustomers.add(row.customer_id);
    const lat = Number(row.latitude);
    const lng = Number(row.longitude);
    primaries.push({
      id: row.id,
      customerId: row.customer_id,
      name: row.customer_name,
      label: row.name || 'Primary Location',
      address: row.address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      location: { lat, lng },
      customerType: row.customer_kind,
    });
  }
  return primaries;
};

interface PointOfInterest {
  id: string;
  name: string;
  type: 'landfill' | 'facility' | 'depot' | 'other';
  address: string;
  location: { lat: number; lng: number };
}

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    height: '100%',
    overflow: 'hidden',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    overflow: 'hidden',
  },
  columnHeader: {
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  columnHeaderWithButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  columnTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  mapContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  columnContent: {
    flex: 1,
    overflowY: 'auto',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  listItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderLeft('3px', 'solid', tokens.colorBrandForeground1),
  },
  productImage: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    objectFit: 'cover',
    flexShrink: 0,
  },
  productIcon: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorBrandBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: '16px',
    flexShrink: 0,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: '14px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  productSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stockItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  stockItemIdentifier: {
    fontSize: '14px',
    fontWeight: '600',
  },
  stockItemProduct: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  stockItemId: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    fontFamily: 'monospace',
    cursor: 'pointer',
    transition: 'color 0.2s',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('40px'),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('40px', '16px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

// Mock data for fleet vehicles
const MOCK_FLEET_VEHICLES: FleetVehicle[] = [
  {
    id: 'fleet-1',
    name: 'Truck 101',
    type: 'Rolloff Truck',
    licensePlate: 'ABC-1234',
    status: 'active',
    location: { lat: 18.4275, lng: -67.1541 },
  },
  {
    id: 'fleet-2',
    name: 'Truck 102',
    type: 'Rolloff Truck',
    licensePlate: 'ABC-1235',
    status: 'active',
    location: { lat: 18.4655, lng: -66.1057 },
  },
  {
    id: 'fleet-3',
    name: 'Truck 103',
    type: 'Front Loader',
    licensePlate: 'ABC-1236',
    status: 'maintenance',
    location: { lat: 18.3808, lng: -65.9574 },
  },
  {
    id: 'fleet-4',
    name: 'Truck 104',
    type: 'Rolloff Truck',
    licensePlate: 'ABC-1237',
    status: 'active',
    location: { lat: 18.2010, lng: -67.1396 },
  },
];

// Mock data for points of interest
const MOCK_POIS: PointOfInterest[] = [
  {
    id: 'poi-1',
    name: 'San Juan Landfill',
    type: 'landfill',
    address: 'Carretera 165, San Juan',
    location: { lat: 18.3855, lng: -66.0655 },
  },
  {
    id: 'poi-2',
    name: 'Bayamón Transfer Station',
    type: 'facility',
    address: 'Ave. Central, Bayamón',
    location: { lat: 18.3996, lng: -66.1630 },
  },
  {
    id: 'poi-3',
    name: 'Equipment Depot',
    type: 'depot',
    address: 'Calle Industrial, Carolina',
    location: { lat: 18.3886, lng: -65.9570 },
  },
  {
    id: 'poi-4',
    name: 'Ponce Recycling Center',
    type: 'facility',
    address: 'PR-2, Ponce',
    location: { lat: 18.0111, lng: -66.6141 },
  },
  {
    id: 'poi-5',
    name: 'Tradewinds Mechanical HQ',
    type: 'facility',
    address: '2 Km 113.2, Bo, Guerrero',
    location: { lat: 18.467239040970536, lng: -67.03670527300969 },
  },
];

const OperationsView: React.FC = () => {
  const styles = useStyles();
  const { debugMode } = useDebug();
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [selectedProductGroupIds, setSelectedProductGroupIds] = useState<Set<string>>(new Set());
  const [selectedStockItemId, setSelectedStockItemId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStockItems, setLoadingStockItems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planningsCache, setPlanningsCache] = useState<Map<string, any>>(new Map());
  const [stockItemsCache, setStockItemsCache] = useState<Map<string, StockItem[]>>(new Map());
  // Imperative handle to the active provider's map (flyTo for center-on-select).
  const mapHandleRef = useRef<MapHandle | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState<Set<string>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [selectedProductGroups, setSelectedProductGroups] = useState<Set<string>>(new Set());
  const [selectedFleetVehicles, setSelectedFleetVehicles] = useState<Set<string>>(new Set());
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [selectedPOIs, setSelectedPOIs] = useState<Set<string>>(new Set());
  const [customerLocations, setCustomerLocations] = useState<CustomerLocation[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);

  // Fetch product groups on mount
  useEffect(() => {
    const fetchProductGroups = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`/booqable/product-groups`);
        const data = await response.json();

        const mappedProducts: ProductGroup[] = data.data.map((item: any) => {
          const attrs = item.attributes;
          return {
            id: item.id,
            name: attrs.name,
            productType: attrs.product_type === 'rental' ? 'Rental product' : attrs.product_type,
            photoUrl: attrs.photo_url,
            sku: attrs.sku || 'N/A',
            stock_count: attrs.stock_count || 0,
          };
        });

        setProductGroups(mappedProducts);
        setError(null);
      } catch (err) {
        setError('Failed to load product groups');
        console.error('Error fetching product groups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductGroups();
  }, []);

  // Fetch saved customer locations on mount (one bulk call instead of N+1).
  useEffect(() => {
    const loadCustomerLocations = async () => {
      try {
        setLoadingCustomers(true);
        const locations = await loadPrimaryCustomerLocations();
        setCustomerLocations(locations);
        console.log(`✓ Loaded ${locations.length} primary customer locations`);
      } catch (err) {
        console.error('Error fetching customer locations:', err);
      } finally {
        setLoadingCustomers(false);
      }
    };

    loadCustomerLocations();
  }, []);

  // Fetch stock items for all selected product groups
  useEffect(() => {
    if (selectedProductGroupIds.size === 0) {
      setStockItems([]);
      return;
    }

    const fetchStockItems = async () => {
      try {
        setLoadingStockItems(true);

        // Fetch stock items for all selected product groups in parallel
        const fetchPromises = Array.from(selectedProductGroupIds).map(async (productGroupId) => {
          // Check cache first
          if (stockItemsCache.has(productGroupId)) {
            console.log('Using cached stock items for product group:', productGroupId);
            return stockItemsCache.get(productGroupId)!;
          }

          // Fetch from API
          console.log('Fetching stock items for product group:', productGroupId);
          const response = await apiFetch(`/booqable/product-group/${productGroupId}`);
          const data = await response.json();

          if (data.error) {
            console.error('Error fetching stock items for product group:', productGroupId, data.error);
            return [];
          }

          const variations = data.variations || [];
          const stockItemsData = data.stock_items || [];
          const productGroup = data.product_group.attributes;

          // Map stock items
          const mappedStockItems: StockItem[] = stockItemsData.map((item: any) => {
            const stockAttrs = item.attributes;
            const variation = variations.find((v: any) => v.id === stockAttrs.product_id);
            const variationAttrs = variation?.attributes || {};

            return {
              id: item.id,
              identifier: stockAttrs.identifier,
              status: stockAttrs.status,
              productId: stockAttrs.product_id,
              productName: variationAttrs.name || productGroup.name,
              productPhotoUrl: variationAttrs.photo_url || productGroup.photo_url,
              availableFrom: stockAttrs.available_from ? formatCalendarDate(stockAttrs.available_from) : undefined,
              availableTill: stockAttrs.available_till ? formatCalendarDate(stockAttrs.available_till) : undefined,
              createdAt: stockAttrs.created_at ? formatBusinessDate(stockAttrs.created_at) : undefined,
              barcode: stockAttrs.barcode || undefined,
              productGroupName: productGroup.name,
              productGroupPhotoUrl: productGroup.photo_url,
            };
          });

          // Cache the stock items
          const newCache = new Map(stockItemsCache);
          newCache.set(productGroupId, mappedStockItems);
          setStockItemsCache(newCache);

          return mappedStockItems;
        });

        // Wait for all fetches to complete
        const allResults = await Promise.all(fetchPromises);

        // Flatten the array of arrays into a single array
        const flattenedStockItems = allResults.flat();

        setStockItems(flattenedStockItems);
      } catch (err) {
        console.error('Error fetching stock items:', err);
        setStockItems([]);
      } finally {
        setLoadingStockItems(false);
      }
    };

    fetchStockItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductGroupIds]);

  const handleProductGroupToggle = (productGroupId: string) => {
    setSelectedProductGroupIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productGroupId)) {
        newSet.delete(productGroupId);
      } else {
        newSet.add(productGroupId);
      }
      return newSet;
    });
  };

  const handleFleetVehicleToggle = (vehicleId: string) => {
    setSelectedFleetVehicles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  const handleCustomerToggle = (customerId: string) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };

  const handlePOIToggle = (poiId: string) => {
    setSelectedPOIs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poiId)) {
        newSet.delete(poiId);
      } else {
        newSet.add(poiId);
      }
      return newSet;
    });
  };

  const handleStockItemClick = (stockItemId: string) => {
    setSelectedStockItemId(stockItemId);
  };

  // Expose function to window for popup buttons
  useEffect(() => {
    (window as any).selectInventoryItem = (itemId: string) => {
      setSelectedStockItemId(itemId);
    };

    return () => {
      delete (window as any).selectInventoryItem;
    };
  }, []);

  // Note: View mode logic removed since we're always showing the map

  const handleCopyUUID = (uuid: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row selection when clicking UUID
    navigator.clipboard.writeText(uuid).then(() => {
      console.log('UUID copied to clipboard:', uuid);
    }).catch(err => {
      console.error('Failed to copy UUID:', err);
    });
  };

  // Get unique variations from stock items
  const getUniqueVariations = () => {
    const variations = new Map<string, string>();
    stockItems.forEach(item => {
      if (item.productId && item.productName) {
        variations.set(item.productId, item.productName);
      }
    });
    return Array.from(variations.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  };

  // Get unique statuses from stock items
  const getUniqueStatuses = () => {
    const statuses = new Set<string>();
    stockItems.forEach(item => {
      if (item.status) {
        statuses.add(item.status);
      }
    });
    return Array.from(statuses).sort();
  };

  // Get unique product groups from stock items (when multiple groups are selected)
  const getUniqueProductGroups = () => {
    const groups = new Map<string, string>();
    stockItems.forEach(item => {
      if (item.productGroupName) {
        // Use productGroupName as both key and value since we don't have a separate ID
        groups.set(item.productGroupName, item.productGroupName);
      }
    });
    return Array.from(groups.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedVariations(new Set());
    setSelectedStatuses(new Set());
    setSelectedProductGroups(new Set());
  };

  // Apply filters to stock items
  const filteredStockItems = stockItems.filter(item => {
    // Filter by status
    if (selectedStatuses.size > 0 && !selectedStatuses.has(item.status)) {
      return false;
    }

    // Filter by variation
    if (selectedVariations.size > 0 && !selectedVariations.has(item.productId)) {
      return false;
    }

    // Filter by product group (when multiple groups are selected)
    if (selectedProductGroups.size > 0 && !selectedProductGroups.has(item.productGroupName || '')) {
      return false;
    }

    return true;
  });

  // Filter out archived items (unless explicitly filtered for)
  const activeStockItems = selectedStatuses.has('archived')
    ? filteredStockItems
    : filteredStockItems.filter(item => item.status !== 'archived');

  // Clear selection if selected item is filtered out
  useEffect(() => {
    if (selectedStockItemId && !activeStockItems.find(item => item.id === selectedStockItemId)) {
      setSelectedStockItemId(null);
    }
  }, [activeStockItems, selectedStockItemId]);

  // Center the map on the selected marker. The active connector exposes a
  // `flyTo` on the MapHandle; we use it instead of touching the underlying
  // map instance directly. Selected-marker highlighting (scale + zIndex) is
  // handled declaratively in the Marker render below — no imperative DOM
  // patching needed.
  useEffect(() => {
    if (!selectedMarkerId || !mapHandleRef.current) return;
    // Find the lat/lng for the selected marker across all layer sources.
    const stockItem = activeStockItems.find((i) => i.id === selectedMarkerId);
    if (stockItem) {
      const { coordinates } = getUniqueLocation(stockItem.id);
      mapHandleRef.current.flyTo({ lat: coordinates[1], lng: coordinates[0] }, 11);
      return;
    }
    const vehicle = MOCK_FLEET_VEHICLES.find((v) => v.id === selectedMarkerId);
    if (vehicle?.location) {
      mapHandleRef.current.flyTo(vehicle.location, 11);
      return;
    }
    const poi = MOCK_POIS.find((p) => p.id === selectedMarkerId);
    if (poi) {
      mapHandleRef.current.flyTo(poi.location, 11);
      return;
    }
    const customer = customerLocations.find((c) => c.id === selectedMarkerId);
    if (customer) {
      mapHandleRef.current.flyTo(customer.location, 11);
      return;
    }
  }, [selectedMarkerId, activeStockItems, customerLocations]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_stock':
        return <Badge appearance="tint" color="success">In Stock</Badge>;
      case 'expected':
        return <Badge appearance="tint" color="warning">Expected</Badge>;
      case 'archived':
        return <Badge appearance="outline" color="subtle">Archived</Badge>;
      default:
        return <Badge appearance="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading product groups..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.loadingContainer}>
        <Text>{error}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Left Column: Map Layers */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>
          <Text className={styles.columnTitle}>Map Layers</Text>
        </div>
        <div className={styles.columnContent}>
          <Accordion multiple collapsible>
            {/* Product Groups Section */}
            <AccordionItem value="product-groups">
              <AccordionHeader>Product Groups ({productGroups.length})</AccordionHeader>
              <AccordionPanel>
                <div style={{ padding: '8px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                  <Button
                    size="small"
                    appearance="transparent"
                    onClick={() => {
                      if (selectedProductGroupIds.size === productGroups.length) {
                        setSelectedProductGroupIds(new Set());
                      } else {
                        setSelectedProductGroupIds(new Set(productGroups.map(pg => pg.id)));
                      }
                    }}
                  >
                    {selectedProductGroupIds.size === productGroups.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {productGroups.map((productGroup) => (
                  <div
                    key={productGroup.id}
                    className={`${styles.listItem} ${selectedProductGroupIds.has(productGroup.id) ? styles.listItemSelected : ''}`}
                    onClick={() => handleProductGroupToggle(productGroup.id)}
                  >
                    <Checkbox
                      checked={selectedProductGroupIds.has(productGroup.id)}
                      onChange={() => handleProductGroupToggle(productGroup.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {productGroup.photoUrl ? (
                      <img
                        src={productGroup.photoUrl}
                        alt={productGroup.name}
                        className={styles.productImage}
                      />
                    ) : (
                      <div className={styles.productIcon}>
                        {productGroup.name.charAt(0)}
                      </div>
                    )}
                    <div className={styles.productInfo}>
                      <div className={styles.productName}>{productGroup.name}</div>
                      <div className={styles.productSubtitle}>{productGroup.productType}</div>
                    </div>
                  </div>

                ))}
              </AccordionPanel>
            </AccordionItem>

            {/* Fleet Vehicles Section */}
            <AccordionItem value="fleet">
              <AccordionHeader>Fleet Vehicles ({MOCK_FLEET_VEHICLES.length})</AccordionHeader>
              <AccordionPanel>
                <div style={{ padding: '8px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                  <Button
                    size="small"
                    appearance="transparent"
                    onClick={() => {
                      if (selectedFleetVehicles.size === MOCK_FLEET_VEHICLES.length) {
                        setSelectedFleetVehicles(new Set());
                      } else {
                        setSelectedFleetVehicles(new Set(MOCK_FLEET_VEHICLES.map(v => v.id)));
                      }
                    }}
                  >
                    {selectedFleetVehicles.size === MOCK_FLEET_VEHICLES.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {MOCK_FLEET_VEHICLES.map((vehicle) => (
                  <div
                    key={vehicle.id}
                    className={`${styles.listItem} ${selectedFleetVehicles.has(vehicle.id) ? styles.listItemSelected : ''}`}
                    onClick={() => handleFleetVehicleToggle(vehicle.id)}
                  >
                    <Checkbox
                      checked={selectedFleetVehicles.has(vehicle.id)}
                      onChange={() => handleFleetVehicleToggle(vehicle.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className={styles.productIcon}>🚛</div>
                    <div className={styles.productInfo}>
                      <div className={styles.productName}>{vehicle.name}</div>
                      <div className={styles.productSubtitle}>{vehicle.type} • {vehicle.licensePlate}</div>
                    </div>
                    <Badge appearance="tint" color={vehicle.status === 'active' ? 'success' : vehicle.status === 'maintenance' ? 'warning' : 'subtle'}>
                      {vehicle.status}
                    </Badge>
                  </div>
                ))}
              </AccordionPanel>
            </AccordionItem>

            {/* Customer Locations Section */}
            <AccordionItem value="customers">
              <AccordionHeader>Customer Locations ({customerLocations.length})</AccordionHeader>
              <AccordionPanel>
                {loadingCustomers ? (
                  <div className={styles.loadingContainer}>
                    <Spinner size="small" label="Loading customers..." />
                  </div>
                ) : customerLocations.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Text>No customer locations available</Text>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: '8px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                      <Button
                        size="small"
                        appearance="transparent"
                        onClick={() => {
                          if (selectedCustomers.size === customerLocations.length) {
                            setSelectedCustomers(new Set());
                          } else {
                            setSelectedCustomers(new Set(customerLocations.map(c => c.id)));
                          }
                        }}
                      >
                        {selectedCustomers.size === customerLocations.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <Accordion multiple collapsible defaultOpenItems={['companies', 'individuals']}>
                      {/* Companies Section */}
                      {customerLocations.filter(c => c.customerType === 'company').length > 0 && (
                        <AccordionItem value="companies">
                          <AccordionHeader expandIconPosition="start">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <span>🏢 Companies ({customerLocations.filter(c => c.customerType === 'company').length})</span>
                              <Button
                                size="small"
                                appearance="transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const companyIds = customerLocations.filter(c => c.customerType === 'company').map(c => c.id);
                                  const allSelected = companyIds.every(id => selectedCustomers.has(id));
                                  if (allSelected) {
                                    setSelectedCustomers(prev => {
                                      const newSet = new Set(prev);
                                      companyIds.forEach(id => newSet.delete(id));
                                      return newSet;
                                    });
                                  } else {
                                    setSelectedCustomers(prev => new Set([...Array.from(prev), ...companyIds]));
                                  }
                                }}
                              >
                                {customerLocations.filter(c => c.customerType === 'company').every(c => selectedCustomers.has(c.id)) ? 'Deselect' : 'Select All'}
                              </Button>
                            </div>
                          </AccordionHeader>
                          <AccordionPanel>
                            {customerLocations
                              .filter(c => c.customerType === 'company')
                              .map((customerLoc) => (
                                <div
                                  key={customerLoc.id}
                                  className={`${styles.listItem} ${selectedCustomers.has(customerLoc.id) ? styles.listItemSelected : ''}`}
                                  onClick={() => handleCustomerToggle(customerLoc.id)}
                                >
                                  <Checkbox
                                    checked={selectedCustomers.has(customerLoc.id)}
                                    onChange={() => handleCustomerToggle(customerLoc.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className={styles.productIcon}>🏢</div>
                                  <div className={styles.productInfo}>
                                    <div className={styles.productName}>{customerLoc.name}</div>
                                    <div className={styles.productSubtitle}>{customerLoc.label}</div>
                                  </div>
                                </div>
                              ))}
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                      {/* Individuals Section */}
                      {customerLocations.filter(c => c.customerType === 'individual').length > 0 && (
                        <AccordionItem value="individuals">
                          <AccordionHeader expandIconPosition="start">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <span>👤 Individuals ({customerLocations.filter(c => c.customerType === 'individual').length})</span>
                              <Button
                                size="small"
                                appearance="transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const individualIds = customerLocations.filter(c => c.customerType === 'individual').map(c => c.id);
                                  const allSelected = individualIds.every(id => selectedCustomers.has(id));
                                  if (allSelected) {
                                    setSelectedCustomers(prev => {
                                      const newSet = new Set(prev);
                                      individualIds.forEach(id => newSet.delete(id));
                                      return newSet;
                                    });
                                  } else {
                                    setSelectedCustomers(prev => new Set([...Array.from(prev), ...individualIds]));
                                  }
                                }}
                              >
                                {customerLocations.filter(c => c.customerType === 'individual').every(c => selectedCustomers.has(c.id)) ? 'Deselect' : 'Select All'}
                              </Button>
                            </div>
                          </AccordionHeader>
                          <AccordionPanel>
                            {customerLocations
                              .filter(c => c.customerType === 'individual')
                              .map((customerLoc) => (
                                <div
                                  key={customerLoc.id}
                                  className={`${styles.listItem} ${selectedCustomers.has(customerLoc.id) ? styles.listItemSelected : ''}`}
                                  onClick={() => handleCustomerToggle(customerLoc.id)}
                                >
                                  <Checkbox
                                    checked={selectedCustomers.has(customerLoc.id)}
                                    onChange={() => handleCustomerToggle(customerLoc.id)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className={styles.productIcon}>👤</div>
                                  <div className={styles.productInfo}>
                                    <div className={styles.productName}>{customerLoc.name}</div>
                                    <div className={styles.productSubtitle}>{customerLoc.label}</div>
                                  </div>
                                </div>
                              ))}
                          </AccordionPanel>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* Points of Interest Section */}
            <AccordionItem value="pois">
              <AccordionHeader>Points of Interest ({MOCK_POIS.length})</AccordionHeader>
              <AccordionPanel>
                <div style={{ padding: '8px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                  <Button
                    size="small"
                    appearance="transparent"
                    onClick={() => {
                      if (selectedPOIs.size === MOCK_POIS.length) {
                        setSelectedPOIs(new Set());
                      } else {
                        setSelectedPOIs(new Set(MOCK_POIS.map(p => p.id)));
                      }
                    }}
                  >
                    {selectedPOIs.size === MOCK_POIS.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                {MOCK_POIS.map((poi) => (
                  <div
                    key={poi.id}
                    className={`${styles.listItem} ${selectedPOIs.has(poi.id) ? styles.listItemSelected : ''}`}
                    onClick={() => handlePOIToggle(poi.id)}
                  >
                    <Checkbox
                      checked={selectedPOIs.has(poi.id)}
                      onChange={() => handlePOIToggle(poi.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className={styles.productIcon}>
                      {poi.type === 'landfill' ? '🗑️' : poi.type === 'facility' ? '🏭' : poi.type === 'depot' ? '🏢' : '📍'}
                    </div>
                    <div className={styles.productInfo}>
                      <div className={styles.productName}>{poi.name}</div>
                      <div className={styles.productSubtitle}>{poi.address}</div>
                    </div>
                  </div>
                ))}
              </AccordionPanel>
            </AccordionItem>

            {/* Routes Section */}
            <AccordionItem value="routes">
              <AccordionHeader>Routes (0)</AccordionHeader>
              <AccordionPanel>
                <div className={styles.emptyState}>
                  <Text>No routes available</Text>
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* TODO: Middle Column - Inventory Items will be re-enabled soon */}
      {/* <div className={styles.column}>
        <div className={styles.columnHeaderWithButton}>
          <Text className={styles.columnTitle}>
            {selectedProductGroupIds.size > 0
              ? `Inventory (${activeStockItems.length} items)`
              : `Inventory`
            }
          </Text>
          {selectedProductGroupIds.size > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {(selectedVariations.size > 0 || selectedStatuses.size > 0 || selectedProductGroups.size > 0) && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                >
                  Clear filters
                </Button>
              )}
              <Menu
                checkedValues={{
                  variation: Array.from(selectedVariations),
                  status: Array.from(selectedStatuses),
                  productGroup: Array.from(selectedProductGroups)
                }}
                onCheckedValueChange={(e, data) => {
                  if (data.name === 'variation') {
                    setSelectedVariations(new Set(data.checkedItems as string[]));
                  } else if (data.name === 'status') {
                    setSelectedStatuses(new Set(data.checkedItems as string[]));
                  } else if (data.name === 'productGroup') {
                    setSelectedProductGroups(new Set(data.checkedItems as string[]));
                  }
                }}
              >
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    appearance="subtle"
                    icon={<FilterRegular />}
                    title="Filter inventory"
                  />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {selectedProductGroupIds.size > 1 && (
                      <>
                        <MenuGroup>
                          <MenuGroupHeader>Product Group</MenuGroupHeader>
                          {getUniqueProductGroups().map(([id, name]) => (
                            <MenuItemCheckbox
                              key={id}
                              name="productGroup"
                              value={name}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getProductGroupColor(name),
                                  }}
                                />
                                {name}
                              </div>
                            </MenuItemCheckbox>
                          ))}
                        </MenuGroup>
                        <MenuDivider />
                      </>
                    )}
                    <MenuGroup>
                      <MenuGroupHeader>Variation</MenuGroupHeader>
                      {getUniqueVariations().map(([id, name]) => (
                        <MenuItemCheckbox
                          key={id}
                          name="variation"
                          value={id}
                        >
                          {name}
                        </MenuItemCheckbox>
                      ))}
                    </MenuGroup>
                    <MenuDivider />
                    <MenuGroup>
                      <MenuGroupHeader>Status</MenuGroupHeader>
                      {getUniqueStatuses().map(status => (
                        <MenuItemCheckbox
                          key={status}
                          name="status"
                          value={status}
                        >
                          {status === 'in_stock' ? 'In Stock' :
                           status === 'expected' ? 'Expected' :
                           status === 'archived' ? 'Archived' :
                           status}
                        </MenuItemCheckbox>
                      ))}
                    </MenuGroup>
                    {(selectedVariations.size > 0 || selectedStatuses.size > 0 || selectedProductGroups.size > 0) && (
                      <>
                        <MenuDivider />
                        <MenuItem onClick={handleClearFilters}>
                          Clear all filters
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          )}
        </div>
        <div className={styles.columnContent}>
          {selectedProductGroupIds.size === 0 ? (
            <div className={styles.emptyState}>
              <Text>Select product groups to view inventory</Text>
            </div>
          ) : loadingStockItems ? (
            <div className={styles.loadingContainer}>
              <Spinner label="Loading inventory..." />
            </div>
          ) : activeStockItems.length === 0 ? (
            <div className={styles.emptyState}>
              <Text>No inventory items found</Text>
            </div>
          ) : (
            activeStockItems.map((stockItem) => (
              <div
                key={stockItem.id}
                className={`${styles.listItem} ${selectedStockItemId === stockItem.id ? styles.listItemSelected : ''}`}
                onClick={() => handleStockItemClick(stockItem.id)}
              >
                <div className={styles.stockItemInfo}>
                  <div className={styles.stockItemIdentifier}>{stockItem.identifier}</div>
                  <div className={styles.stockItemProduct}>{stockItem.productName}</div>
                  {selectedProductGroupIds.size > 1 && stockItem.productGroupName && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '11px',
                      color: tokens.colorNeutralForeground3,
                      marginTop: '2px'
                    }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: getProductGroupColor(stockItem.productGroupName),
                          flexShrink: 0,
                        }}
                      />
                      {stockItem.productGroupName}
                    </div>
                  )}
                  {debugMode && (
                    <div
                      className={styles.stockItemId}
                      onClick={(e) => handleCopyUUID(stockItem.id, e)}
                      title="Click to copy UUID"
                    >
                      {stockItem.id}
                    </div>
                  )}
                </div>
                {getStatusBadge(stockItem.status)}
              </div>
            ))
          )}
        </div>
      </div> */}

      {/* Middle Column: Map View */}
      <div style={{
        backgroundColor: tokens.colorNeutralBackground1,
        height: '100%',
        overflow: 'hidden'
      }}>
        <div className={styles.mapContainer} style={{ position: 'relative' }}>
          <MapView
            center={{ lat: 18.2208, lng: -66.5901 }}
            zoom={9}
            style="streets"
            onLoad={() => setMapLoaded(true)}
            mapRef={(h) => { mapHandleRef.current = h; }}
            style_={{ width: '100%', height: '100%' }}
          >
            {/* Stock item markers (one per product group / variation). */}
            {activeStockItems.map((item) => {
              const location = getUniqueLocation(item.id);
              const markerColor =
                selectedProductGroupIds.size > 1 && item.productGroupName
                  ? getProductGroupColor(item.productGroupName)
                  : '#0078d4';
              const isSelected = selectedMarkerId === item.id;
              const thumbnailUrl = item.productPhotoUrl || item.productGroupPhotoUrl;
              return (
                <Marker
                  key={item.id}
                  position={{ lat: location.coordinates[1], lng: location.coordinates[0] }}
                  color={markerColor}
                  scale={isSelected ? 1.2 : 1}
                  zIndex={isSelected ? 1000 : undefined}
                  onClick={() => setSelectedMarkerId(item.id)}
                  popup={
                    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", padding: 4 }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={item.productName}
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 80,
                              height: 80,
                              background: `linear-gradient(135deg, ${markerColor} 0%, ${markerColor}dd 100%)`,
                              borderRadius: 4,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 28,
                              fontWeight: 'bold',
                            }}
                          >
                            {item.identifier.charAt(0)}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{item.identifier}</div>
                          <div style={{ fontSize: 12, color: '#605e5c' }}>{item.productName}</div>
                          {selectedProductGroupIds.size > 1 && item.productGroupName && (
                            <div style={{ fontSize: 12, color: '#8a8886', marginBottom: 4 }}>🏷️ {item.productGroupName}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                  popupOpen={isSelected}
                />
              );
            })}

            {/* Fleet vehicle markers — always-on truck tooltip via `tooltip`. */}
            {MOCK_FLEET_VEHICLES.filter((v) => selectedFleetVehicles.has(v.id)).map((vehicle) => {
              if (!vehicle.location) return null;
              const isSelected = selectedMarkerId === vehicle.id;
              return (
                <Marker
                  key={vehicle.id}
                  position={vehicle.location}
                  color="#FF8C00"
                  scale={isSelected ? 1.2 : 1}
                  zIndex={isSelected ? 1000 : undefined}
                  onClick={() => setSelectedMarkerId(vehicle.id)}
                  tooltip={`🚛 ${vehicle.name}`}
                />
              );
            })}

            {/* POI markers. */}
            {MOCK_POIS.filter((poi) => selectedPOIs.has(poi.id)).map((poi) => {
              const markerColor =
                poi.type === 'landfill'
                  ? '#D13438'
                  : poi.type === 'facility'
                    ? '#8764B8'
                    : poi.type === 'depot'
                      ? '#107C10'
                      : '#00B7C3';
              const isSelected = selectedMarkerId === poi.id;
              return (
                <Marker
                  key={poi.id}
                  position={poi.location}
                  color={markerColor}
                  scale={isSelected ? 1.2 : 1}
                  zIndex={isSelected ? 1000 : undefined}
                  onClick={() => setSelectedMarkerId(poi.id)}
                  popup={
                    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", padding: 4 }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                        <div style={{ fontSize: 32 }}>📍</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{poi.name}</div>
                          <div style={{ fontSize: 12, color: '#605e5c' }}>{poi.address}</div>
                          <div style={{ fontSize: 11, color: '#8a8886', textTransform: 'capitalize' }}>{poi.type}</div>
                        </div>
                      </div>
                    </div>
                  }
                  popupOpen={isSelected}
                />
              );
            })}

            {/* Customer location markers. */}
            {customerLocations.filter((c) => selectedCustomers.has(c.id)).map((customerLoc) => {
              const isSelected = selectedMarkerId === customerLoc.id;
              return (
                <Marker
                  key={customerLoc.id}
                  position={customerLoc.location}
                  color="#00B294"
                  scale={isSelected ? 1.2 : 1}
                  zIndex={isSelected ? 1000 : undefined}
                  onClick={() => setSelectedMarkerId(customerLoc.id)}
                  popup={
                    <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", padding: 4 }}>
                      <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                        <div style={{ fontSize: 32 }}>👤</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{customerLoc.name}</div>
                          <div style={{ fontSize: 12, color: '#605e5c' }}>{customerLoc.label}</div>
                          <div style={{ fontSize: 11, color: '#8a8886' }}>{customerLoc.address}</div>
                        </div>
                      </div>
                    </div>
                  }
                  popupOpen={isSelected}
                />
              );
            })}
          </MapView>
          {!mapLoaded && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: tokens.colorNeutralBackground2,
              }}
            >
              <Spinner size="medium" label="Loading map…" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OperationsView;
